<script lang="ts">
	import { Heading } from "flowbite-svelte";
	import { Badge } from "flowbite-svelte-v1";
	import { twMerge } from "tailwind-merge";
	import type { DetailItem, LandingPage } from "#gen/bundles/v1/bundle_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import GradientImageCarousel from "#lib/components/flow/viewer/desktop/GradientImageCarousel.svelte";
	import { getTextColor } from "#lib/theme.js";
	import type { Snippet } from "svelte";

	interface Props {
		landingPage: LandingPage;
		hasPrimaryConversion?: boolean;
		shouldPromoteFirstApp?: boolean;
		conversion?: Snippet;
		isLightBackground?: boolean;
	}

	const { landingPage, hasPrimaryConversion = true, shouldPromoteFirstApp = false, conversion, isLightBackground = false }: Props = $props();

	const hasRightHandContent = $derived(landingPage.primaryText.length || landingPage.secondaryText?.text || landingPage.tertiaryText?.length || landingPage.highlightText || hasPrimaryConversion || shouldPromoteFirstApp);

	function shouldCenter(highlightText: DetailItem | undefined, tertiaryText: DetailItem[]) {
		if (highlightText && tertiaryText.length > 1) {
			return false;
		}
		if (!highlightText && tertiaryText.length > 2) {
			return false;
		}
		return true;
	}
</script>

<div class="relative mt-1!">
	<div class={twMerge("mx-auto flex h-full w-full max-w-215 flex-row items-center justify-between gap-2 px-6 py-8", shouldPromoteFirstApp ? "items-start" : "items-center", hasRightHandContent ? "" : "justify-center")}>
		<div class="flex aspect-square w-1/2 max-w-90 items-center justify-center rounded-xl">
			<GradientImageCarousel {landingPage} />
		</div>
		{#if hasRightHandContent}
			<div class="flex w-1/2 flex-col items-start justify-center gap-2 pr-2 pl-8">
				<Heading tag="h1" class="text-left text-[2rem] leading-[2.2rem] font-bold" style={isLightBackground ? "color: black;" : "color: var(--header-color);"}>{landingPage.primaryText}</Heading>
				{#if landingPage.secondaryText && landingPage.secondaryText.text}
					{@const paragraphFallback = isLightBackground ? "#1f2937" : "#e5e7eb"}
					<div class="flex-none text-left text-gray-300">
						<p class="ellipsis mb-1 text-left text-base font-normal" style="color: var(--paragraph-color, {paragraphFallback});">
							{#if landingPage.secondaryText.icon}
								<Icon class="inline-block align-text-top" iconSet={landingPage.secondaryText.iconSet} name={landingPage.secondaryText.icon} />
							{/if}
							{landingPage.secondaryText.text}
						</p>
					</div>
				{/if}
				<div class="flex flex-nowrap items-center gap-2" class:justify-center={shouldCenter(landingPage.highlightText, landingPage.tertiaryText)}>
					{#if landingPage.highlightText}
						{@const color = landingPage.highlightText?.colorHex || "#374151"}
						<Badge large rounded class="flex flex-none items-center gap-1 px-3 py-1 text-sm" style="background-color: {color}; color: {getTextColor(color)};">
							{landingPage.highlightText.text}
						</Badge>
					{/if}
					{#each landingPage.tertiaryText as item (item)}
						{#if item.text || item.icon}
							{@const color = item.colorHex || "#374151"}
							<Badge rounded class="flex flex-none items-center gap-1 px-3 py-1 text-sm" style="background-color: {color}; color: {getTextColor(color)};">
								{#if item.icon}
									<Icon iconSet={item.iconSet} name={item.icon} />
								{/if}
								{item.text}
							</Badge>
						{/if}
					{/each}
				</div>
				<div class="mt-6 w-75">
					{@render conversion?.()}
				</div>
			</div>
		{/if}
	</div>
</div>
